/**
 * چیدمانِ ستون‌ها برای جدول‌های دستی (نه GxTable) — جابه‌جایی، عرض، و
 * **جاساز کردن در یک صفحه**.
 *
 * چرا جدا از GxTable: گریدِ «ثبت کارکرد» جدولِ ساختهٔ خودش است (سلولِ ورودی،
 * ردیفِ جمع، ستون‌های ساخته‌شده از نرخ‌نامه) و نمی‌تواند GxTable شود؛ ولی کاربر
 * انتظار دارد ستون‌هایش هم مثلِ بقیهٔ جدول‌های برنامه رفتار کنند.
 *
 * ذخیره per-user است (`layoutStore` روی همان کلیدِ کاربرِ واردشده) پس هر نفر
 * چیدمانِ خودش را دارد و با رفرش برنمی‌گردد.
 *
 * **fit**: اگر `fit` بدهید، عرضِ ستون‌هایی که کاربر دستی تنظیمشان نکرده طوری
 * حساب می‌شود که کلِ جدول در عرضِ موجود جا شود (تا کفِ `minWidth`). اگر باز هم
 * جا نشد، `compact` برمی‌گردد تا صداکننده فونت/پدینگ را کوچک کند. این همان
 * «نخواهیم مدام چپ‌وراست اسکرول کنیم» است.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GxHeaderCell, measureIntrinsicWidth } from "./gxHeaderCell";
import {
  loadOrder, saveOrder, columnsSignature, syncSignature,
} from "../utils/layoutStore";

const idOf = (c, i) => String(c.gxId ?? c.key ?? c.dataIndex ?? `__pos${i}`);

export default function useColumnLayout(layoutId, columns, opts = {}) {
  const {
    fit = null,          // {fixed: {id: width}, minWidth, maxWidth, compactBelow}
    enabled = true,
    alwaysOn = [],       // ستون‌هایی که خاموش‌کردنشان معنی ندارد (#، تعداد، دکمه‌ها)
  } = opts;

  const wrapRef = useRef(null);
  const ids = useMemo(() => columns.map(idOf), [columns]);

  const [order, setOrder] = useState(() => loadOrder("col", layoutId));
  const [widths, setWidths] = useState(() => loadOrder("wid", layoutId) || {});
  // ستون‌های خاموش — برگهٔ شلوغ را خلوت می‌کند و per user می‌ماند
  const [hidden, setHidden] = useState(() => new Set(loadOrder("hid", layoutId) || []));
  const widthsRef = useRef(widths);
  useEffect(() => { widthsRef.current = widths; }, [widths]);
  /* همان قاعدهٔ GxTable: اگر ستون‌ها عوض شده‌اند، چیدمانِ کهنه دور ریخته شود.
     وگرنه ترتیب/عرضِ ذخیره‌شده به شناسه‌های قدیمی اشاره می‌کند و گرید می‌پاشد. */
  const sig = useMemo(() => columnsSignature(ids), [ids]);
  useEffect(() => {
    /* `ids` پاس داده می‌شود تا «کدام ستون‌ها را بسته‌ام» و عرض‌ها **هرس** شوند
       نه پاک. در گریدِ عملیات، ستون‌ها با تأخیر می‌آیند (نرخ‌نامه async است و
       ستون‌های دستی بعداً برمی‌گردند)، پس امضا در هر بار باز کردن یک‌بار عوض
       می‌شد و بدونِ هرس، حافظهٔ همان محصول هر بار از صفر شروع می‌کرد. */
    syncSignature(layoutId, sig);
    setOrder(loadOrder("col", layoutId));
    setWidths(loadOrder("wid", layoutId) || {});
    setHidden(new Set(loadOrder("hid", layoutId) || []));
  }, [layoutId, sig]);
  // چیدمانِ سرور رسید ⇒ دوباره بخوان
  useEffect(() => {
    const h = () => {
      setOrder(loadOrder("col", layoutId));
      setWidths(loadOrder("wid", layoutId) || {});
      setHidden(new Set(loadOrder("hid", layoutId) || []));
    };
    window.addEventListener("gx-prefs-hydrated", h);
    return () => window.removeEventListener("gx-prefs-hydrated", h);
  }, [layoutId]);

  const toggleHidden = useCallback((colId, show) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (show) next.delete(colId); else next.add(colId);
      saveOrder("hid", layoutId, [...next]);
      return next;
    });
  }, [layoutId]);

  const showAll = useCallback(() => {
    saveOrder("hid", layoutId, []);
    setHidden(new Set());
  }, [layoutId]);

  // ── عرضِ در دسترس (برای جاساز کردن) ──
  const [avail, setAvail] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !fit || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setAvail(Math.floor(e.contentRect.width)));
    ro.observe(el);
    setAvail(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [fit]);

  const orderedAll = useMemo(() => {
    if (!order || !order.length) return columns;
    const byId = new Map(columns.map((c, i) => [ids[i], c]));
    const used = new Set();
    const out = [];
    order.forEach((oid) => { if (byId.has(oid)) { out.push(byId.get(oid)); used.add(oid); } });
    columns.forEach((c, i) => { if (!used.has(ids[i])) out.push(c); });
    return out;
  }, [columns, ids, order]);

  // شناسه یک‌بار روی لیستِ کامل حساب می‌شود؛ بعد از فیلترکردن ایندکس عوض
  // می‌شود و `idOf(c, i)` می‌تواند شناسهٔ اشتباه بدهد.
  const idByCol = useMemo(() => {
    const m = new Map();
    orderedAll.forEach((c, i) => m.set(c, idOf(c, i)));
    return m;
  }, [orderedAll]);
  const cid = useCallback((c) => idByCol.get(c), [idByCol]);

  // ستون‌های همیشه‌روشن حتی اگر در حافظه خاموش مانده باشند دیده می‌شوند
  const keep = useMemo(() => new Set(alwaysOn), [alwaysOn]);
  const ordered = useMemo(
    () => orderedAll.filter((c) => keep.has(cid(c)) || !hidden.has(cid(c))),
    [orderedAll, hidden, keep, cid]);

  const move = useCallback((from, to) => {
    // روی لیستِ کامل جابه‌جا می‌شود، وگرنه ستونِ خاموش جایش را از دست می‌دهد
    const cur = orderedAll.map(cid);
    const fi = cur.indexOf(from);
    const ti = cur.indexOf(to);
    if (fi < 0 || ti < 0) return;
    cur.splice(ti, 0, cur.splice(fi, 1)[0]);
    saveOrder("col", layoutId, cur);
    setOrder(cur);
  }, [orderedAll, cid, layoutId]);

  const resize = useCallback((colId, w, commit) => {
    if (commit) { saveOrder("wid", layoutId, widthsRef.current); return; }
    setWidths((prev) => {
      const next = { ...prev, [colId]: w };
      widthsRef.current = next;
      return next;
    });
  }, [layoutId]);

  /** دابل‌کلیکِ لبه = اندازهٔ خودکار بر اساسِ محتوای سلول‌ها (مثلِ اکسل) */
  const autoFit = useCallback((colId) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const headers = Array.from(wrap.querySelectorAll(".ant-table-thead th"));
    const idx = headers.findIndex((th) => th.getAttribute("data-gx-id") === colId);
    if (idx < 0) return;
    let w = 0;
    const rows = wrap.querySelectorAll(".ant-table-tbody > tr");
    for (let i = 0; i < Math.min(rows.length, 60); i++) {
      w = Math.max(w, measureIntrinsicWidth(rows[i].children[idx]));
    }
    const next = { ...widthsRef.current, [colId]: Math.min(420, Math.max(56, w + 10)) };
    widthsRef.current = next;
    saveOrder("wid", layoutId, next);
    setWidths(next);
  }, [layoutId]);

  const reset = useCallback(() => {
    saveOrder("col", layoutId, []);
    saveOrder("wid", layoutId, {});
    saveOrder("hid", layoutId, []);
    setOrder(null);
    setWidths({});
    setHidden(new Set());
    widthsRef.current = {};
  }, [layoutId]);

  // ── جاساز کردن: عرضِ خودکارِ ستون‌هایی که کاربر دست نزده ──
  const autoWidth = useMemo(() => {
    if (!fit || !avail) return null;
    const fixed = fit.fixed || {};
    const flexIds = ordered.map(cid).filter((i) => fixed[i] == null);
    if (!flexIds.length) return null;
    const taken = ordered.map(cid).reduce((a, i) => a + (fixed[i] || 0), 0);
    // ستون‌هایی که کاربر خودش عرضشان را تنظیم کرده، سهمشان قطعی است
    const userSet = flexIds.filter((i) => widths[i] != null);
    const userTaken = userSet.reduce((a, i) => a + widths[i], 0);
    const free = flexIds.length - userSet.length;
    if (free <= 0) return null;
    const space = avail - taken - userTaken - 18;   // ۱۸ = حاشیهٔ اطمینان
    const w = Math.floor(space / free);
    return Math.max(fit.minWidth || 90, Math.min(fit.maxWidth || 200, w));
  }, [fit, avail, ordered, widths, cid]);

  const compact = !!(fit && autoWidth != null && autoWidth <= (fit.compactBelow || 0));

  const cols = useMemo(() => ordered.map((c) => {
    const id = cid(c);
    const fixedW = fit && fit.fixed ? fit.fixed[id] : undefined;
    const w = widths[id] ?? fixedW ?? autoWidth ?? c.width;
    return {
      ...c,
      ...(w ? { width: w } : {}),
      onHeaderCell: () => ({
        gxId: id,
        gxMove: enabled ? move : undefined,
        gxResize: enabled ? resize : undefined,
        gxAutoFit: enabled ? autoFit : undefined,
      }),
    };
  }), [ordered, widths, autoWidth, fit, move, resize, autoFit, enabled, cid]);

  const components = useMemo(() => ({ header: { cell: GxHeaderCell } }), []);

  return {
    columns: cols, components, wrapRef, reset, compact, ordered,
    // برای ساختنِ منویِ «تنظیماتِ ستون‌ها» در صداکننده
    allColumns: orderedAll, hidden, toggleHidden, showAll, cid,
  };
}
